import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { getCurrentUser, signOut } from '@/lib/supabase'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Mail, Settings, LogOut, ArrowRightLeft, Server, Users, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'

const navigation = [
  { name: 'Migrations', href: '/dashboard/migrations', icon: ArrowRightLeft },
  { name: 'Emails',     href: '/dashboard/emails',     icon: Users          },
  { name: 'Web Files',  href: '/dashboard/web-files',  icon: Globe          },
  { name: 'Servers',    href: '/dashboard/servers',    icon: Server         },
  { name: 'Settings',   href: '/dashboard/settings',   icon: Settings       },
]

export default function DashboardLayout() {
  const [user, setUser] = useState(null)
  const location  = useLocation()
  const navigate  = useNavigate()

  useEffect(() => {
    const loadUser = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('dev') === '1') {
        setUser({ email: 'dev@example.com', id: 'dev' })
        return
      }
      const currentUser = await getCurrentUser()
      if (!currentUser) { navigate('/login'); return }
      setUser(currentUser)
    }
    loadUser()
  }, [navigate])

  const handleSignOut = async () => { await signOut(); navigate('/login') }
  const getInitials  = (email) => email?.charAt(0).toUpperCase() || 'U'

  /* ── Loading splash ── */
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-primary/50 border-t-primary animate-spin" />
          <p className="text-sm text-muted-foreground tracking-wide">Loading…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">

      {/* ── Frosted-glass sidebar ──────────────────────────────── */}
      <aside className="hidden lg:flex flex-col fixed top-0 left-0 z-40 h-screen w-56 glass-heavy">

        {/* Logo */}
        <div className="h-14 flex items-center px-5 shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2.5 group">
            <div className="
              w-7 h-7 rounded-lg flex items-center justify-center
              bg-primary shadow-sm
              transition-all duration-200 group-hover:scale-105 group-hover:shadow-md
            ">
              <Mail className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-foreground/90">
              EmailMigrate
            </span>
          </Link>
        </div>

        {/* Hairline divider */}
        <div className="mx-4 h-px bg-black/5" />

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => {
            const Icon     = item.icon
            const isActive = location.pathname.startsWith(item.href)
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'group flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium',
                  'transition-all duration-150',
                  isActive
                    ? [
                        'bg-white text-primary',
                        'shadow-[0_1px_0_rgba(255,255,255,1)_inset,0_1px_4px_rgba(0,0,0,0.06)]',
                      ].join(' ')
                    : 'text-foreground/50 hover:text-foreground/75 hover:bg-white/60'
                )}
              >
                <Icon className={cn(
                  'w-4 h-4 shrink-0 transition-colors',
                  isActive ? 'text-primary' : 'text-foreground/40 group-hover:text-foreground/60'
                )} />
                {item.name}
                {isActive && (
                  <span className="ml-auto w-1 h-4 rounded-full bg-primary opacity-80" />
                )}
              </Link>
            )
          })}
        </nav>

        {/* Hairline divider */}
        <div className="mx-4 h-px bg-black/5" />

        {/* User menu */}
        <div className="p-3 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="
                w-full flex items-center gap-2.5 px-2 py-2 rounded-lg
                text-sm hover:bg-white/70 transition-colors duration-150
              ">
                <Avatar className="w-7 h-7 shrink-0">
                  <AvatarFallback className="text-[11px] font-semibold bg-primary/[0.10] text-primary">
                    {getInitials(user.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left overflow-hidden">
                  <p className="text-[12px] font-medium text-foreground/80 truncate leading-tight">
                    {user.email}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    Administrator
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-52 mb-1 glass-dialog border-0">
              <DropdownMenuLabel className="text-[11px] text-muted-foreground font-normal py-2">
                Signed in as
                <span className="block font-medium text-foreground/80 truncate mt-0.5">
                  {user.email}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-black/6" />
              <DropdownMenuItem
                onClick={() => navigate('/dashboard/settings')}
                className="text-[13px] cursor-pointer"
              >
                <Settings className="mr-2 h-3.5 w-3.5" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-black/6" />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="text-destructive focus:text-destructive text-[13px] cursor-pointer"
              >
                <LogOut className="mr-2 h-3.5 w-3.5" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* ── Main area ─────────────────────────────────────────── */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen">

        {/* Mobile top bar */}
        <header className="lg:hidden h-14 glass border-b border-white/40 flex items-center px-4 sticky top-0 z-30">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
              <Mail className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold">EmailMigrate</span>
          </Link>
        </header>

        {/* Page content */}
        <main className="flex-1 p-5 lg:p-8">
          <div className="page-enter max-w-5xl mx-auto w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
